import React from 'react';
import { useIntl } from 'react-intl';

import { PerformanceInsight, PerformanceTotal } from 'apis/merchant-gateway/insights';

import SummaryTable from './SummaryTable';
import { useInsightSubRoutes } from '../../hooks/useInsightsSubRoutes';
import { InsightResponseWithPreviousInterval, JsonPath, MetricRow, RowType } from '../../types';
import { calculateGrowthRate, formatGrowthRate, getGrowthTrend } from '../../utils';

type CustomerBaseProps = {
  performanceInsight: InsightResponseWithPreviousInterval<PerformanceInsight>;
};

const rowsConfig: {
  title: JsonPath;
  titleTooltip: JsonPath;
  getValue: (total: PerformanceTotal) => number;
}[] = [
  {
    title: 'insights.customer-base.total-customers',
    titleTooltip: 'insights.total-customers.tooltip',
    getValue: v => v.firstTimeCustomersCount + v.returningCustomersCount,
  },
  {
    title: 'insights.customer-base.first-time',
    titleTooltip: 'insights.first-time.tooltip',
    getValue: v => v.firstTimeCustomersCount,
  },
  {
    title: 'insights.customer-base.returning',
    titleTooltip: 'insights.returning.tooltip',
    getValue: v => v.returningCustomersCount,
  },
];

const CustomerBase = ({ performanceInsight }: CustomerBaseProps) => {
  const {
    selected: { data: selectedInsight },
    previous: { data: previousInsight },
  } = performanceInsight;
  const { customerBaseLink } = useInsightSubRoutes();
  const { formatMessage, locale } = useIntl();

  const selectedTotal = selectedInsight?.data.subscription.total;
  const previousTotal = previousInsight?.data.subscription.total;

  const rows = rowsConfig.map(({ getValue, ...row }): MetricRow => {
    const metrics = selectedTotal ? getValue(selectedTotal) : 0;
    const previousMetrics = previousTotal ? getValue(previousTotal) : 0;

    return {
      ...row,
      metrics,
      growthRate: formatGrowthRate(calculateGrowthRate(previousMetrics, metrics), locale),
      growthTrend: getGrowthTrend(calculateGrowthRate(previousMetrics, metrics)),
      type: RowType.Metric as const,
    };
  });

  return (
    <SummaryTable
      isFetching={performanceInsight.isFetching}
      isError={performanceInsight.isError}
      onRetry={performanceInsight.refetch}
      title={formatMessage({ id: 'insights.customer-base.header' })}
      tooltip={formatMessage({ id: 'insights.customer-base.tooltip' })}
      link={customerBaseLink}
      rows={rows}
    />
  );
};

export default CustomerBase;
